// src/modules/moduleDashboards/components/actions/EodControlWidget.jsx
//
// The actionable End of Day panel. Three jobs, in the order they matter:
//
//   1. Show where today stands — opening cash, in, out, expected closing, and
//      (once counted) the difference.
//   2. Surface anything BLOCKING today. Unclosed previous days are what produce
//      the "previous business day is still OPEN" wall, so they are named here
//      with their expected cash rather than left for the user to discover.
//   3. Offer the action that fits: start EOD, go and count, or — for users
//      carrying the day-closure override — force-close a stuck day.
//
// Force-close is deliberately NOT one click. It writes a permanent CLOSED day
// with a recorded discrepancy, so the modal makes you enter the physical
// denomination count (pre-filled with what the ledger expects), shows the
// difference you are about to record, and requires a written reason. The server
// re-checks override authorisation regardless of what this UI shows.
import React, { useMemo, useState } from 'react';
import {
  Card, Button, Tag, Space, Typography, Modal, Input, InputNumber,
  Alert, Tooltip, message, Divider, DatePicker,
} from 'antd';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRightOutlined, LockOutlined, WarningFilled, CheckCircleFilled,
  FieldTimeOutlined, ThunderboltOutlined, LeftOutlined, RightOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { initiateEod, forceCloseDay, forceCloseAllDays } from '../../../treasury/api';
import { BranchPicker, NeedsBranch } from './BranchPicker';
import { useWidgetView } from './useWidgetView';

const { Text } = Typography;

const INK = { primary: 'rgba(0,0,0,0.88)', secondary: 'rgba(0,0,0,0.55)', muted: 'rgba(0,0,0,0.35)' };
const GRID = 'rgba(0,0,0,0.07)';
const TONE = { success: '#0f7b52', danger: '#c0362f', warning: '#b26a00', info: '#1f5fa8', default: INK.primary };

const STATUS_TAG = {
  OPEN: { color: 'blue', label: 'OPEN' },
  REOPENED: { color: 'gold', label: 'REOPENED' },
  PENDING_CASH_VERIFICATION: { color: 'orange', label: 'COUNTING CASH' },
  PENDING_CHECKER_APPROVAL: { color: 'orange', label: 'AWAITING APPROVAL' },
  CLOSED: { color: 'green', label: 'CLOSED' },
  HOLIDAY_AUTO_CLOSED: { color: 'green', label: 'HOLIDAY — AUTO CLOSED' },
  NO_DAY: { color: 'default', label: 'NOT OPENED' },
};

// Note values, largest first — the order a cashier counts in.
const NOTES = [
  ['note2000', 2000], ['note500', 500], ['note200', 200], ['note100', 100], ['note50', 50],
  ['note20', 20], ['note10', 10], ['note5', 5], ['note2', 2], ['note1', 1],
];

const inr = (v) => Number(v || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 });
const countsTotal = (c) => NOTES.reduce((sum, [key, denom]) => sum + (Number(c?.[key]) || 0) * denom, 0);

function Metric({ label, value, tone, hint }) {
  return (
    <div style={{ background: '#fff', padding: '10px 12px', minWidth: 0 }}>
      <Text style={{ fontSize: 11, color: INK.secondary, display: 'block' }} ellipsis>{label}</Text>
      <div style={{ fontSize: 17, fontWeight: 650, color: TONE[tone] || INK.primary, lineHeight: 1.3, marginTop: 2 }}>
        ₹{inr(value)}
      </div>
      {hint && <Text style={{ fontSize: 10, color: INK.muted }}>{hint}</Text>}
    </div>
  );
}

/* ── Force-close modal ─────────────────────────────────── */

function ForceCloseModal({ open, day, all, branchId, onClose, onDone }) {
  const [counts, setCounts] = useState({});
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();

  // Start from what the ledger expects, so a balanced close is a confirmation
  // rather than 10 fields of data entry — but every field stays editable.
  React.useEffect(() => {
    if (open) {
      const seed = {};
      NOTES.forEach(([k]) => { seed[k] = day?.expectedDenominations?.[k] || 0; });
      setCounts(seed);
      setReason('');
    }
  }, [open, day]);

  const physical = countsTotal(counts);
  const expected = day?.expectedCash || 0;
  const difference = Math.round((physical - expected) * 100) / 100;

  const submit = async () => {
    if (!reason.trim()) { messageApi.warning('A reason is required — this is an audited override'); return; }
    setSaving(true);
    try {
      const payload = { branchId, counts, reason };
      const res = all
        ? await forceCloseAllDays(payload)
        : await forceCloseDay({ ...payload, businessDate: day.businessDateStr || day.businessDate });
      messageApi.success(res?.data?.message || 'Day closed');
      onDone?.();
      onClose();
    } catch (err) {
      messageApi.error(err?.response?.data?.message || 'Force close failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      onOk={submit}
      confirmLoading={saving}
      okText={all ? 'Force close all days' : 'Force close this day'}
      okButtonProps={{ danger: true }}
      width={560}
      title={(
        <Space>
          <LockOutlined style={{ color: TONE.danger }} />
          <span>{all ? 'Force close all blocking days' : `Force close ${day?.businessDateStr || ''}`}</span>
        </Space>
      )}
    >
      {contextHolder}
      <Alert
        type="warning" showIcon style={{ marginBottom: 14 }}
        message="This skips checker approval"
        description={all
          ? 'Earlier days auto-balance at their own expected cash; the count below applies to the most recent day, which carries any difference. Every day is marked force-closed against your name.'
          : 'The day closes immediately as CLOSED and is marked force-closed against your name. Any difference is recorded permanently for audit.'}
      />

      <Text strong style={{ fontSize: 13 }}>Physical cash count</Text>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(104px, 1fr))', gap: 8, margin: '8px 0 12px' }}>
        {NOTES.map(([key, denom]) => (
          <div key={key}>
            <Text style={{ fontSize: 11, color: INK.secondary }}>₹{denom}</Text>
            <InputNumber
              min={0} size="small" style={{ width: '100%' }}
              value={counts[key]}
              onChange={(v) => setCounts((c) => ({ ...c, [key]: v || 0 }))}
            />
          </div>
        ))}
      </div>

      <div style={{ background: '#fafafa', border: `1px solid ${GRID}`, borderRadius: 8, padding: '10px 12px' }}>
        <Space size={24} wrap>
          <span><Text style={{ fontSize: 11, color: INK.secondary, display: 'block' }}>Counted</Text>
            <Text strong style={{ fontVariantNumeric: 'tabular-nums' }}>₹{inr(physical)}</Text></span>
          <span><Text style={{ fontSize: 11, color: INK.secondary, display: 'block' }}>Expected</Text>
            <Text strong style={{ fontVariantNumeric: 'tabular-nums' }}>₹{inr(expected)}</Text></span>
          <span><Text style={{ fontSize: 11, color: INK.secondary, display: 'block' }}>Difference</Text>
            <Text strong style={{ color: difference === 0 ? TONE.success : TONE.danger, fontVariantNumeric: 'tabular-nums' }}>
              {difference > 0 ? '+' : ''}₹{inr(difference)} {difference === 0 ? '· balanced' : difference > 0 ? '· excess' : '· short'}
            </Text></span>
        </Space>
      </div>

      <Divider style={{ margin: '14px 0 10px' }} />
      <Text strong style={{ fontSize: 13 }}>Reason for the override <Text type="danger">*</Text></Text>
      <Input.TextArea
        rows={3} style={{ marginTop: 6 }}
        placeholder="Why is this day being closed outside the approval workflow?"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
      />
    </Modal>
  );
}

/* ── Widget ────────────────────────────────────────────── */

export default function EodControlWidget({ widgetId, title, route, data, onRefresh }) {
  const navigate = useNavigate();
  const [messageApi, contextHolder] = message.useMessage();
  const [starting, setStarting] = useState(false);
  const [forceModal, setForceModal] = useState({ open: false, day: null, all: false });

  // The widget owns its branch AND its operating date: the page filter seeds the
  // branch, the in-card controls override either, and only this card refetches.
  const { payload, branchId, setBranch, setBusinessDate, reload, loading } = useWidgetView({ widgetId, data });

  const target = route || '/treasury/eod-closure';
  const s = payload?.summary;
  const blocking = useMemo(() => payload?.blocking || [], [payload]);
  const status = payload?.dayStatus || 'NO_DAY';
  const tag = STATUS_TAG[status] || STATUS_TAG.NO_DAY;
  const viewDate = payload?.businessDate ? dayjs(payload.businessDate) : dayjs();
  const isToday = payload?.isToday !== false;
  // EOD is started on the current operating day; a past day is shown read-only
  // here and cleared through force-close (or the full page) instead.
  const canStart = isToday && ['OPEN', 'REOPENED'].includes(status) && blocking.length === 0;

  const shiftDay = (days) => setBusinessDate(viewDate.add(days, 'day').format('YYYY-MM-DD'));

  // After acting, refresh this card first (instant, correct) and the rest of the
  // dashboard after — the other EOD widgets read the same day.
  const refreshAll = () => { reload(); onRefresh?.(); };

  if (payload?.needsBranch) {
    return (
      <Card size="small" title={title} style={{ height: '100%', borderRadius: 12 }}>
        <NeedsBranch
          branches={payload.branches}
          value={branchId}
          onChange={setBranch}
          loading={loading}
          what="End of Day closure"
        />
      </Card>
    );
  }

  const startEod = async () => {
    setStarting(true);
    try {
      await initiateEod({ branchId: payload.branchId, businessDate: payload.businessDate });
      messageApi.success('EOD started — enter the denomination count to continue');
      refreshAll();
      navigate(target);
    } catch (err) {
      messageApi.error(err?.response?.data?.message || 'Could not start EOD');
    } finally {
      setStarting(false);
    }
  };

  return (
    <Card
      size="small"
      style={{ height: '100%', borderRadius: 12 }}
      styles={{ header: { minHeight: 40, borderBottom: `1px solid ${GRID}` }, body: { padding: 12 } }}
      title={(
        <Space size={8} wrap>
          <span style={{ fontSize: 13, fontWeight: 600, color: INK.primary }}>{title}</span>
          <Tag color={tag.color} style={{ marginInlineEnd: 0 }}>{tag.label}</Tag>
          {payload?.canOverride && (
            <Tooltip title="You hold the day-closure override — you can force-close stuck days">
              <Tag icon={<LockOutlined />} color="purple" style={{ marginInlineEnd: 0 }}>override</Tag>
            </Tooltip>
          )}
        </Space>
      )}
      extra={(
        <Space size={6} wrap>
          {payload?.branches?.length ? (
            <BranchPicker branches={payload.branches} value={branchId} onChange={setBranch} loading={loading} width={165} />
          ) : (
            <Text strong style={{ fontSize: 12 }}>{payload?.branchName}</Text>
          )}

          {/* Operating date. Stepping back a day is the common move (checking or
              clearing yesterday), so it gets its own button rather than a calendar. */}
          <Space.Compact>
            <Tooltip title="Previous day">
              <Button size="small" icon={<LeftOutlined />} onClick={() => shiftDay(-1)} disabled={loading} />
            </Tooltip>
            <DatePicker
              size="small"
              value={viewDate}
              onChange={(d) => setBusinessDate((d || dayjs()).format('YYYY-MM-DD'))}
              format="DD/MM/YYYY"
              allowClear={false}
              disabled={loading}
              // No EOD exists for a day that hasn't happened.
              disabledDate={(d) => d && d > dayjs().endOf('day')}
              style={{ width: 122 }}
            />
            <Tooltip title={isToday ? 'Already on today' : 'Next day'}>
              <Button size="small" icon={<RightOutlined />} onClick={() => shiftDay(1)} disabled={loading || isToday} />
            </Tooltip>
          </Space.Compact>

          {!isToday && (
            <Tooltip title="Back to today">
              <Button size="small" type="link" onClick={() => setBusinessDate(dayjs().format('YYYY-MM-DD'))}>
                Today
              </Button>
            </Tooltip>
          )}
        </Space>
      )}
    >
      {contextHolder}

      {/* Say plainly when the figures below are not today's. */}
      {!isToday && (
        <Alert
          type="info" showIcon style={{ marginBottom: 12 }}
          message={`Viewing ${viewDate.format('DD MMM YYYY')} — not the current day`}
          description="These are that day's figures. End of Day is started on the current day; a stuck past day is cleared with force-close below."
        />
      )}

      {/* Cash position for the selected day */}
      {s && (
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 1,
          background: GRID, border: `1px solid ${GRID}`, borderRadius: 10, overflow: 'hidden', marginBottom: 12,
        }}>
          <Metric label="Opening Cash" value={s.openingCash} />
          <Metric label="Cash In" value={s.totalCashIn} tone="success" />
          <Metric label="Cash Out" value={s.totalCashOut} tone="warning" />
          <Metric label="Expected Closing" value={s.expectedCash} tone="info" />
          {s.countedCash !== null && s.countedCash !== undefined && (
            <Metric label="Counted" value={s.countedCash} />
          )}
          {s.difference !== null && s.difference !== undefined && (
            <Metric
              label="Difference" value={s.difference}
              tone={s.difference === 0 ? 'success' : 'danger'}
              hint={s.difference === 0 ? 'balanced' : s.difference > 0 ? 'excess' : 'short'}
            />
          )}
        </div>
      )}

      {/* Blocking previous days — the reason today won't move */}
      {blocking.length > 0 ? (
        <div style={{ border: `1px solid ${TONE.danger}33`, background: `${TONE.danger}0A`, borderRadius: 10, padding: 10, marginBottom: 12 }}>
          <Space size={6} style={{ marginBottom: 8 }}>
            <WarningFilled style={{ color: TONE.danger }} />
            <Text strong style={{ fontSize: 12, color: TONE.danger }}>
              {blocking.length} previous day{blocking.length > 1 ? 's are' : ' is'} still open — today is blocked
            </Text>
          </Space>

          {blocking.map((b) => (
            <div key={b.businessDayId} style={{
              display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
              padding: '6px 0', borderTop: `1px solid ${GRID}`,
            }}>
              <div style={{ flex: '1 1 180px', minWidth: 0 }}>
                <Text strong style={{ fontSize: 12 }}>{b.businessDateStr || new Date(b.businessDate).toLocaleDateString('en-IN')}</Text>
                <Text style={{ fontSize: 11, color: INK.secondary, display: 'block' }}>
                  expects ₹{inr(b.expectedCash)} · {String(b.status).replaceAll('_', ' ')}
                  {b.daysStuck ? ` · stuck ${b.daysStuck}d` : ''}
                </Text>
              </div>
              {payload?.canOverride ? (
                <Button size="small" danger icon={<LockOutlined />}
                  onClick={() => setForceModal({ open: true, day: b, all: false })}>
                  Force close
                </Button>
              ) : (
                <Tooltip title="Only users granted the day-closure override in Treasury Settings can force-close a stuck day">
                  <Button size="small" disabled icon={<LockOutlined />}>Force close</Button>
                </Tooltip>
              )}
            </div>
          ))}

          {payload?.canOverride && blocking.length > 1 && (
            <Button
              size="small" danger type="primary" block style={{ marginTop: 8 }}
              icon={<ThunderboltOutlined />}
              onClick={() => setForceModal({ open: true, day: blocking[blocking.length - 1], all: true })}
            >
              Force close all {blocking.length} days
            </Button>
          )}
        </div>
      ) : (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px', marginBottom: 12,
          borderRadius: 8, background: `${TONE.success}0F`, color: TONE.success, fontSize: 12,
        }}>
          <CheckCircleFilled />
          <span>No previous days blocking today</span>
        </div>
      )}

      {/* What to do next */}
      <Space wrap style={{ width: '100%' }}>
        {canStart && (
          <Button type="primary" icon={<FieldTimeOutlined />} loading={starting} onClick={startEod}>
            Start End of Day
          </Button>
        )}
        <Button
          type={canStart ? 'default' : 'primary'}
          ghost={!canStart}
          icon={<ArrowRightOutlined />}
          onClick={() => navigate(target)}
        >
          {status === 'PENDING_CASH_VERIFICATION' ? 'Count cash & submit' : 'Open EOD Closure'}
        </Button>
      </Space>

      <ForceCloseModal
        open={forceModal.open}
        day={forceModal.day}
        all={forceModal.all}
        branchId={payload?.branchId}
        onClose={() => setForceModal({ open: false, day: null, all: false })}
        onDone={refreshAll}
      />
    </Card>
  );
}
